import { useState, useEffect } from 'react';
import { 
  Upload, FileText, Trash2, User, Home, Shield, CheckCircle, 
  Package, Phone, CreditCard, Loader2, ArrowRight, Flame, Cloud, Zap, Scan, Download
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { useAutoSave } from '../../../hooks/useAutoSave';

const MissionFormTab = ({ user, activeSubscription, setActiveTab }) => {
  const [formData, setFormData] = useState({
    // Déménagement
    currentAddress: '',
    currentCity: '',
    currentPostalCode: '',
    newAddress: '',
    newCity: '',
    newPostalCode: '',
    movingDate: '',
    contractsToTransfer: [],
    // Héritage
    deceasedName: '',
    deceasedBirthDate: '',
    deceasedDeathDate: '',
    relationToDeceased: '',
    contractsToCancel: [],
  });
  const [deathCertificate, setDeathCertificate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  
  const packType = activeSubscription?.pack_type;
  const isDemenagement = packType === 'mission_demenagement' || packType === 'nouveau_depart';
  const isHeritage = packType === 'mission_heritage' || packType === 'serenite_succession';
  const isAudit = packType === 'mission_audit' || packType === 'audit_pouvoir_achat';

  // II.1 — Auto-save brouillon dans localStorage (filet de sécurité au-dessus du save serveur)
  // Clé unique par user + pack_type pour ne pas écraser entre missions
  const autoSaveKey = `mission_form_${user?.user_id || 'anon'}_${packType || 'unknown'}`;
  const { restored: draftRestored, clear: clearDraft } = useAutoSave(autoSaveKey, formData, setFormData);
  
  // 🎫 FIX: Charger les données existantes de la mission au montage
  useEffect(() => {
    const fetchExistingData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/missions/details`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        
        if (res.data.has_details && res.data.details) {
          const details = res.data.details;
          
          // Restaurer les données déménagement
          if (details.current_address || details.new_address) {
            setFormData(prev => ({
              ...prev,
              currentAddress: details.current_address?.address || '',
              currentCity: details.current_address?.city || '',
              currentPostalCode: details.current_address?.postal_code || '',
              newAddress: details.new_address?.address || '',
              newCity: details.new_address?.city || '',
              newPostalCode: details.new_address?.postal_code || '',
              movingDate: details.moving_date || '',
              contractsToTransfer: details.contracts_to_transfer || []
            }));
          }
          
          // Restaurer les données succession
          if (details.deceased) {
            setFormData(prev => ({
              ...prev,
              deceasedName: details.deceased?.name || '',
              deceasedBirthDate: details.deceased?.birth_date || '',
              deceasedDeathDate: details.deceased?.death_date || '',
              relationToDeceased: details.family?.lien_parente || '',
              contractsToCancel: details.contracts_to_cancel || []
            }));
          }
        }
      } catch (error) {
        console.log('Pas de données existantes à charger');
      } finally {
        setLoadingData(false);
      }
    };
    
    fetchExistingData();
  }, []);
  
  // Liste des contrats possibles
  const availableContracts = [
    { id: 'edf', name: 'EDF / Électricité', icon: Zap },
    { id: 'gaz', name: 'Gaz (Engie, etc.)', icon: Flame },
    { id: 'eau', name: 'Eau', icon: Cloud },
    { id: 'internet', name: 'Internet / Box', icon: Phone },
    { id: 'mobile', name: 'Mobile', icon: Phone },
    { id: 'assurance_habitation', name: 'Assurance Habitation', icon: Home },
    { id: 'assurance_auto', name: 'Assurance Auto', icon: Shield },
    { id: 'banque', name: 'Compte Bancaire', icon: CreditCard },
    { id: 'mutuelle', name: 'Mutuelle Santé', icon: Shield },
    { id: 'autres', name: 'Autres abonnements', icon: Package },
  ];
  
  const handleContractToggle = (contractId, field) => {
    setFormData(prev => {
      const contracts = prev[field];
      if (contracts.includes(contractId)) {
        return { ...prev, [field]: contracts.filter(c => c !== contractId) };
      } else {
        return { ...prev, [field]: [...contracts, contractId] };
      }
    });
  };
  
  const handleDeathCertificateUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('document_type', 'death_certificate');
      
      await axios.post(`${API}/documents/upload`, formDataUpload, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      setDeathCertificate(file.name);
      toast.success('Acte de décès téléchargé avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };
  
  const [mandatUrl, setMandatUrl] = useState(null);
  const [generatingMandat, setGeneratingMandat] = useState(false);
  
  const handleSaveMissionDetails = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      // 1. Sauvegarder les détails de la mission
      await axios.post(`${API}/missions/details`, {
        pack_type: packType,
        ...formData
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      
      // 🔴 P0 FIX : Générer automatiquement le mandat après la sauvegarde
      setGeneratingMandat(true);
      try {
        const mandatRes = await axios.post(`${API}/mandats/generate-from-mission`, {
          pack_type: packType
        }, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        
        if (mandatRes.data.mandat_url) {
          setMandatUrl(mandatRes.data.mandat_url);
          toast.success('Mission enregistrée ! Votre mandat est prêt à télécharger.');
        } else {
          toast.success('Mission enregistrée ! Le mandat sera disponible sous peu.');
        }
        // II.1 — Le formulaire a bien été persisté côté serveur, on peut purger le brouillon local
        clearDraft();
      } catch (mandatError) {
        console.log('Erreur génération mandat:', mandatError);
        toast.success('Mission enregistrée ! Vous pouvez générer le mandat manuellement.');
        clearDraft();
      } finally {
        setGeneratingMandat(false);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };
  
  // 🔴 P0 FIX : Charger le mandat existant au montage
  useEffect(() => {
    const fetchExistingMandat = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/mandats/latest`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        if (res.data.mandat_url) {
          setMandatUrl(res.data.mandat_url);
        }
      } catch (error) {
        // Pas de mandat existant
      }
    };
    fetchExistingMandat();
  }, []);
  
  // Si c'est un audit, rediriger vers les documents
  if (isAudit) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="font-orbitron text-2xl font-bold text-white mb-2">Mission Audit</h2>
          <p className="text-gray-400 font-mono text-sm">// SCAN_RELEVÉS_BANCAIRES</p>
        </div>
        <div className="glass-card p-6 border border-success/30 bg-success/5">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-success/20 rounded-lg flex items-center justify-center">
              <Scan className="w-6 h-6 text-success" />
            </div>
            <div>
              <h3 className="font-orbitron text-lg font-bold text-success">Prêt pour l'Audit</h3>
              <p className="text-gray-400 text-sm">Uploadez vos relevés bancaires pour démarrer l'analyse</p>
            </div>
          </div>
          <Button onClick={() => setActiveTab('documents')} className="w-full bg-success text-black hover:bg-success/90">
            <Upload className="w-4 h-4 mr-2" />Uploader mes Relevés
          </Button>
        </div>
      </div>
    );
  }
  
  // Formulaire Déménagement
  if (isDemenagement) {
    if (loadingData) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          <span className="ml-3 text-gray-400">Chargement des données...</span>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        <div>
          <h2 className="font-orbitron text-2xl font-bold text-white mb-2">Mission Déménagement</h2>
          <p className="text-gray-400 font-mono text-sm">// COLLECTE_INFORMATIONS</p>
        </div>

        {/* II.1 — Indicateur brouillon restauré */}
        {draftRestored && (
          <div className="glass-card border border-cyan-400/30 p-3 flex items-center justify-between">
            <span className="text-xs text-cyan-400">
              Brouillon restauré · sauvegarde automatique toutes les 30s
            </span>
            <button onClick={clearDraft} className="text-xs text-gray-500 hover:text-white underline">
              Effacer le brouillon
            </button>
          </div>
        )}

        {/* Adresse Actuelle */}
        <div className="glass-card p-6 border border-orange-500/30">
          <h3 className="font-orbitron text-lg font-bold text-orange-400 mb-4 flex items-center gap-2">
            <Home className="w-5 h-5" /> Adresse Actuelle (à quitter)
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-gray-400 text-sm">Adresse complète</Label>
              <Input 
                value={formData.currentAddress}
                onChange={(e) => setFormData({...formData, currentAddress: e.target.value})}
                placeholder="123 rue de la Paix"
                className="bg-gray-900 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Code Postal</Label>
              <Input 
                value={formData.currentPostalCode}
                onChange={(e) => setFormData({...formData, currentPostalCode: e.target.value})}
                placeholder="75001"
                className="bg-gray-900 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Ville</Label>
              <Input 
                value={formData.currentCity}
                onChange={(e) => setFormData({...formData, currentCity: e.target.value})}
                placeholder="Paris"
                className="bg-gray-900 border-gray-700 text-white mt-1"
              />
            </div>
          </div>
        </div>
        
        {/* Nouvelle Adresse */}
        <div className="glass-card p-6 border border-cyan-400/30">
          <h3 className="font-orbitron text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
            <ArrowRight className="w-5 h-5" /> Nouvelle Adresse (destination)
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-gray-400 text-sm">Adresse complète</Label>
              <Input 
                value={formData.newAddress}
                onChange={(e) => setFormData({...formData, newAddress: e.target.value})}
                placeholder="456 avenue des Champs"
                className="bg-gray-900 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Code Postal</Label>
              <Input 
                value={formData.newPostalCode}
                onChange={(e) => setFormData({...formData, newPostalCode: e.target.value})}
                placeholder="31000"
                className="bg-gray-900 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Ville</Label>
              <Input 
                value={formData.newCity}
                onChange={(e) => setFormData({...formData, newCity: e.target.value})}
                placeholder="Toulouse"
                className="bg-gray-900 border-gray-700 text-white mt-1"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-gray-400 text-sm">Date de déménagement</Label>
              <Input 
                type="date"
                value={formData.movingDate}
                onChange={(e) => setFormData({...formData, movingDate: e.target.value})}
                className="bg-gray-900 border-gray-700 text-white mt-1"
              />
            </div>
          </div>
        </div>
        
        {/* Contrats à transférer */}
        <div className="glass-card p-6 border border-gray-800">
          <h3 className="font-orbitron text-lg font-bold text-white mb-4">Contrats à Transférer/Résilier</h3>
          <p className="text-gray-400 text-sm mb-4">Sélectionnez les contrats que vous souhaitez que nous gérions :</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {availableContracts.map(contract => {
              const IconComp = contract.icon;
              const isSelected = formData.contractsToTransfer.includes(contract.id);
              return (
                <button
                  key={contract.id}
                  onClick={() => handleContractToggle(contract.id, 'contractsToTransfer')}
                  className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 text-sm ${
                    isSelected 
                      ? 'border-cyan-400 bg-cyan-400/10 text-cyan-400' 
                      : 'border-gray-700 hover:border-gray-600 text-gray-400'
                  }`}
                >
                  <IconComp className="w-4 h-4" />
                  {contract.name}
                  {isSelected && <CheckCircle className="w-4 h-4 ml-auto" />}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* 🔴 P0 FIX : Section Mandat téléchargeable */}
        {mandatUrl && (
          <div className="glass-card p-6 border-2 border-success/50 bg-success/5">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-success/20 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-success" />
              </div>
              <div>
                <h3 className="font-orbitron text-lg font-bold text-success">Mandat Disponible</h3>
                <p className="text-gray-400 text-sm">Votre mandat a été généré avec succès</p>
              </div>
            </div>
            <a 
              href={mandatUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-success text-black font-bold rounded hover:bg-success/90 transition-colors"
              data-testid="download-mandat-btn"
            >
              <Download className="w-5 h-5" />
              Télécharger mon Mandat
            </a>
          </div>
        )}
        
        {/* 🔴 P0 FIX : Bouton de sauvegarde renommé */}
        <Button 
          onClick={handleSaveMissionDetails}
          disabled={saving || generatingMandat || !formData.currentAddress || !formData.newAddress || !formData.movingDate}
          className="w-full py-6 bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron"
          data-testid="save-mission-btn"
        >
          {saving || generatingMandat ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {generatingMandat ? 'Génération du mandat...' : 'Enregistrement...'}
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              {mandatUrl ? 'Mettre à jour les informations' : 'Valider et Générer le Mandat'}
            </>
          )}
        </Button>
      </div>
    );
  }
  
  // Formulaire Héritage
  if (isHeritage) {
    if (loadingData) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-magenta-400 animate-spin" />
          <span className="ml-3 text-gray-400">Chargement des données...</span>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        <div>
          <h2 className="font-orbitron text-2xl font-bold text-white mb-2">Mission Héritage</h2>
          <p className="text-gray-400 font-mono text-sm">// GESTION_SUCCESSION</p>
        </div>
        
        {/* Informations sur le défunt */}
        <div className="glass-card p-6 border border-magenta-400/30">
          <h3 className="font-orbitron text-lg font-bold text-magenta-400 mb-4 flex items-center gap-2">
            <User className="w-5 h-5" /> Informations sur le Défunt
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-gray-400 text-sm">Nom complet du défunt</Label>
              <Input 
                value={formData.deceasedName}
                onChange={(e) => setFormData({...formData, deceasedName: e.target.value})}
                placeholder="Jean Dupont"
                className="bg-gray-900 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Date de naissance</Label>
              <Input 
                type="date"
                value={formData.deceasedBirthDate}
                onChange={(e) => setFormData({...formData, deceasedBirthDate: e.target.value})}
                className="bg-gray-900 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Date de décès</Label>
              <Input 
                type="date"
                value={formData.deceasedDeathDate}
                onChange={(e) => setFormData({...formData, deceasedDeathDate: e.target.value})}
                className="bg-gray-900 border-gray-700 text-white mt-1"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-gray-400 text-sm">Votre lien avec le défunt</Label>
              <select
                value={formData.relationToDeceased}
                onChange={(e) => setFormData({...formData, relationToDeceased: e.target.value})}
                className="w-full bg-gray-900 border border-gray-700 text-white rounded-md p-2 mt-1"
              >
                <option value="">Sélectionner...</option>
                <option value="spouse">Conjoint(e)</option>
                <option value="child">Enfant</option>
                <option value="parent">Parent</option>
                <option value="sibling">Frère/Sœur</option>
                <option value="executor">Exécuteur testamentaire</option>
                <option value="notary">Notaire</option>
                <option value="other">Autre</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Upload Acte de Décès */}
        <div className="glass-card p-6 border-2 border-dashed border-magenta-400/50 hover:border-magenta-400 transition-colors">
          <h3 className="font-orbitron text-lg font-bold text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-magenta-400" /> Acte de Décès (obligatoire)
          </h3>
          {deathCertificate ? (
            <div className="flex items-center gap-3 p-4 bg-magenta-400/10 rounded-lg">
              <CheckCircle className="w-6 h-6 text-magenta-400" />
              <div className="flex-1">
                <p className="text-white font-medium">{deathCertificate}</p>
                <p className="text-magenta-400 text-sm">Document téléchargé</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setDeathCertificate(null)}
                className="text-red-400 hover:text-red-300"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center py-8 cursor-pointer">
              <Upload className="w-12 h-12 text-magenta-400 mb-3" />
              <p className="text-white font-medium mb-1">Cliquez pour uploader l'acte de décès</p>
              <p className="text-gray-500 text-sm">PDF ou image (max 10 Mo)</p>
              <input 
                type="file" 
                className="hidden" 
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleDeathCertificateUpload}
                disabled={uploading}
              />
              {uploading && <Loader2 className="w-6 h-6 text-magenta-400 animate-spin mt-4" />}
            </label>
          )}
        </div>
        
        {/* Contrats à résilier */}
        <div className="glass-card p-6 border border-gray-800">
          <h3 className="font-orbitron text-lg font-bold text-white mb-4">Contrats du Défunt à Résilier</h3>
          <p className="text-gray-400 text-sm mb-4">Sélectionnez tous les contrats connus (nous rechercherons les autres) :</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {availableContracts.map(contract => {
              const IconComp = contract.icon;
              const isSelected = formData.contractsToCancel.includes(contract.id);
              return (
                <button
                  key={contract.id}
                  onClick={() => handleContractToggle(contract.id, 'contractsToCancel')}
                  className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 text-sm ${
                    isSelected 
                      ? 'border-magenta-400 bg-magenta-400/10 text-magenta-400' 
                      : 'border-gray-700 hover:border-gray-600 text-gray-400'
                  }`}
                >
                  <IconComp className="w-4 h-4" />
                  {contract.name}
                  {isSelected && <CheckCircle className="w-4 h-4 ml-auto" />}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* 🔴 P0 FIX : Section Mandat téléchargeable */}
        {mandatUrl && (
          <div className="glass-card p-6 border-2 border-success/50 bg-success/5">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-success/20 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-success" />
              </div>
              <div>
                <h3 className="font-orbitron text-lg font-bold text-success">Mandat Disponible</h3>
                <p className="text-gray-400 text-sm">Votre mandat a été généré avec succès</p>
              </div>
            </div>
            <a 
              href={mandatUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-success text-black font-bold rounded hover:bg-success/90 transition-colors"
              data-testid="download-mandat-heritage-btn"
            >
              <Download className="w-5 h-5" />
              Télécharger mon Mandat
            </a>
          </div>
        )}
        
        {/* 🔴 P0 FIX : Bouton de sauvegarde renommé */}
        <Button 
          onClick={handleSaveMissionDetails}
          disabled={saving || generatingMandat || !formData.deceasedName || !deathCertificate}
          className="w-full py-6 bg-magenta-400 text-white hover:bg-magenta-500 font-orbitron"
          data-testid="save-mission-heritage-btn"
        >
          {saving || generatingMandat ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {generatingMandat ? 'Génération du mandat...' : 'Enregistrement...'}
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              {mandatUrl ? 'Mettre à jour les informations' : 'Valider et Générer le Mandat'}
            </>
          )}
        </Button>
      </div>
    );
  }
  
  // Fallback
  return (
    <div className="text-center py-12">
      <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
      <p className="text-gray-400">Veuillez d'abord sélectionner une mission</p>
      <Button onClick={() => setActiveTab('payment')} className="mt-4 bg-cyan-400 text-black">
        Choisir une Mission
      </Button>
    </div>
  );
};

export { MissionFormTab };
